import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Menu,
  MenuItem,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import { Info, ChevronDown } from 'lucide-react';
import { EXTERNAL_URLS } from '../config/urls';

// Real payment data from registration rRdeor0zddpgsydCHY4X
const samplePayments = [
  {
    // Original payment (REAL DATA)
    date: new Date(1769193796 * 1000), // Convert from timestamp
    method: 'stripe',
    amount: 280,
    comment: 'stripe:checkout_session:cs_test_a1byHCfoPnQu0bI4jqcAZVqJcUWecJpwesSVz5HSArbW9RDXuT9JgoUAsd',
    stripeSessionId: 'cs_test_a1byHCfoPnQu0bI4jqcAZVqJcUWecJpwesSVz5HSArbW9RDXuT9JgoUAsd',
    paymentIntentId: 'pi_3SspK6BXxwmutP6903gnWUFc',
    chargeId: 'py_3SspK6BXxwmutP690hSARkJ0', // This is actually a PaymentIntent, not charge
    paymentMethodType: 'link',
    paymentMethodDetails: {
      type: 'link'
    },
    billingDetails: {
      name: ' fjsdak',
      email: 'torgeir.kruke@gmail.com',
      phone: null,
      address: {
        line1: 'f jdsal',
        city: 'fads',
        country: 'NO',
        postal_code: '7777'
      }
    },
    currency: 'nok',
    receiptUrl: 'https://pay.stripe.com/receipts/payment/CAcaFwoVYWNjdF8xU3B0UlZCWHh3bXV0UDY5KMSCz8sGMgbzWqX8xi86LBbivKsMx5pgEoeAeHQCtdtIzD9yIPPswVMjEVhmyEuE_D0KemA1wMfhzOrc'
  },
  {
    // Refund (REAL DATA - AFTER FIX!)
    date: new Date(1769193863 * 1000), // Convert from timestamp
    amount: -30,
    method: 'stripe_refund',
    note: 'Refund approved',
    adminTaskId: 'FLTt3Y1wUExPQV8tWb1W',
    // ✅ NOW HAS REQUIRED FIELDS:
    chargeId: 'py_3SspK6BXxwmutP690hSARkJ0',
    refundId: 're_fixed_1769197870884',
    receiptUrl: 'https://pay.stripe.com/receipts/payment/CAcaFwoVYWNjdF8xU3B0UlZCWHh3bXV0UDY5KMSCz8sGMgbzWqX8xi86LBbivKsMx5pgEoeAeHQCtdtIzD9yIPPswVMjEVhmyEuE_D0KemA1wMfhzOrc'
  }
];

const ReceiptListTestPage: React.FC = () => {
  const [receiptMenuAnchor, setReceiptMenuAnchor] = useState<null | HTMLElement>(null);
  const [receiptMenuPayments, setReceiptMenuPayments] = useState<any[]>([]);

  const formatPaymentDate = (payment: any): string => {
    if (!payment.date) return 'No date';
    const date = payment.date instanceof Date ? payment.date : new Date(payment.date);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getPaymentIcon = (payment: any): string => {
    return payment.method === 'stripe_refund' ? '↩' : '📅';
  };

  const getPaymentColor = (payment: any): string => {
    return payment.amount < 0 || payment.method === 'stripe_refund' ? 'error.main' : 'inherit';
  };

  const getPaymentLabel = (payment: any): string => {
    if (payment.method === 'stripe_refund') {
      return ' (Refund)';
    }
    if (payment.paymentMethodDetails?.card) {
      return ` (${payment.paymentMethodDetails.card.brand} •••• ${payment.paymentMethodDetails.card.last4})`;
    }
    return '';
  };

  const openReceiptUrl = (payment: any) => {
    if (payment.receiptUrl) {
      window.open(payment.receiptUrl, '_blank');
    } else if (payment.chargeId) {
      const isTest = payment.chargeId.startsWith('ch_test_');
      const baseUrl = isTest ? EXTERNAL_URLS.stripe.testDashboard : EXTERNAL_URLS.stripe.dashboard;
      const dashboardUrl = `${baseUrl}/payments/${payment.chargeId}`;
      window.open(dashboardUrl, '_blank');
    }
  };

  const handleGetReceipt = (event: React.MouseEvent<HTMLElement>) => {
    console.log('=== DEBUG: Receipt List Filtering ===');
    console.log('1. All payments:', samplePayments);
    
    // Step 1: Filter by method
    const methodFiltered = samplePayments.filter((p: any) => p.method === 'stripe' || p.method === 'stripe_refund');
    console.log('2. After method filter (stripe/stripe_refund):', methodFiltered);
    
    // Step 2: Filter by receiptUrl or chargeId
    const urlFiltered = methodFiltered.filter((p: any) => p.receiptUrl || p.chargeId);
    console.log('3. After receiptUrl/chargeId filter:', urlFiltered);
    
    // Step 3: Add isRefund flag
    const paymentsAndRefunds = urlFiltered.map((p: any) => ({
      ...p,
      isRefund: p.amount < 0 || p.method === 'stripe_refund'
    }));
    console.log('4. Final payments with isRefund flag:', paymentsAndRefunds);
    
    // Additional debug info
    console.log('=== Payment Details ===');
    samplePayments.forEach((payment, idx) => {
      console.log(`Payment ${idx + 1}:`, {
        method: payment.method,
        amount: payment.amount,
        hasReceiptUrl: !!payment.receiptUrl,
        hasChargeId: !!payment.chargeId,
        receiptUrl: payment.receiptUrl,
        chargeId: payment.chargeId,
        isRefund: payment.amount < 0 || payment.method === 'stripe_refund'
      });
    });
    
    setReceiptMenuPayments(paymentsAndRefunds);
    setReceiptMenuAnchor(event.currentTarget);
  };

  const handleReceiptMenuClose = () => {
    setReceiptMenuAnchor(null);
    setReceiptMenuPayments([]);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Receipt List Test Page
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        This page helps debug the receipt list display. Click the button below to see how payments and refunds are rendered.
      </Alert>

      {/* Test Button */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Test Receipt Dropdown
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handleGetReceipt}
          sx={{ textTransform: 'none' }}
          startIcon={<Info size={16} />}
          endIcon={<ChevronDown size={14} />}
        >
          Get Receipt
        </Button>
        
        <Menu
          anchorEl={receiptMenuAnchor}
          open={Boolean(receiptMenuAnchor)}
          onClose={handleReceiptMenuClose}
        >
          {receiptMenuPayments.map((payment, idx) => (
            <MenuItem
              key={idx}
              onClick={() => {
                openReceiptUrl(payment);
                handleReceiptMenuClose();
              }}
              sx={{
                color: getPaymentColor(payment)
              }}
            >
              {getPaymentIcon(payment)} {formatPaymentDate(payment)} - {Math.abs(payment.amount)} NOK
              {getPaymentLabel(payment)}
            </MenuItem>
          ))}
        </Menu>
      </Paper>

      {/* Raw Data Display */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Raw Payment Data" />
            <CardContent>
              {samplePayments.map((payment, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="primary">
                    Payment #{idx + 1} - {payment.method}
                  </Typography>
                  <Box component="pre" sx={{ 
                    fontSize: '0.75rem', 
                    bgcolor: 'grey.100', 
                    p: 1, 
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 200
                  }}>
                    {JSON.stringify(payment, null, 2)}
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Expected Visual Output" />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Here's how the receipt list should look:
              </Typography>
              
              {samplePayments.map((payment, idx) => (
                <Box key={idx} sx={{ 
                  p: 1, 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  mb: 1,
                  color: getPaymentColor(payment)
                }}>
                  <Typography variant="body2">
                    {getPaymentIcon(payment)} {formatPaymentDate(payment)} - {Math.abs(payment.amount)} NOK{getPaymentLabel(payment)}
                  </Typography>
                </Box>
              ))}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="text.secondary">
                <strong>Debug Info:</strong>
              </Typography>
              <ul style={{ fontSize: '0.875rem', margin: 0, paddingLeft: 20 }}>
                <li>Payment count: {samplePayments.length}</li>
                <li>Filtered count: {samplePayments.filter(p => p.method === 'stripe' || p.method === 'stripe_refund').length}</li>
                <li>With receiptUrl/chargeId: {samplePayments.filter(p => p.receiptUrl || p.chargeId).length}</li>
                <li>Refunds: {samplePayments.filter(p => p.method === 'stripe_refund').length}</li>
              </ul>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReceiptListTestPage;
